import React from 'react'
import { InView } from 'react-intersection-observer'
import { GuildAccessPill } from 'src/molecules/GuildAccessPill'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { TitleAndSubtitleDisplay } from '@/atoms/TitleAndSubtitleDisplay'
import { GuildAccessNotch } from '@/molecules/GuildAccessNotch'
import { LandscapeTileCard } from '@/molecules/LandscapeRowCollection'
import {
  DiscoverItemInteraction,
  useContentInteraction,
  HOVER_THRESHOLD_MS,
} from '@/services/Analytics/ContentInteractions'
import { Episode, Project } from '@/services/ProjectsService'
import { useRecommendationTracking } from '@/services/RecommendedBannerService/useRecommendationTracking'
import { LinkViewModel } from '@/services/RenderService'
import { useSafeTrack } from '@/utils/analytics'

export interface TopPicksLinkViewModel extends LinkViewModel {
  eyebrow?: string
  subtitle?: string
  watchableAt?: string | null
  __typename?: string | null
  attribution?: unknown
}

export interface TopPickCardProps {
  contentLink: TopPicksLinkViewModel
  itemIndex: number
  isGuildMember: boolean
  loading: boolean
  hoverStartTimeRef: React.MutableRefObject<Map<string, number>>
  impressionTimeoutRef: React.MutableRefObject<Map<string, NodeJS.Timeout>>
  hasTrackedImpression: React.MutableRefObject<Set<string>>
  createInteraction: (contentLink: TopPicksLinkViewModel, itemIndex: number) => DiscoverItemInteraction | null
}

export const TopPickCard: React.FC<TopPickCardProps> = ({
  contentLink,
  itemIndex,
  isGuildMember,
  loading,
  hoverStartTimeRef,
  impressionTimeoutRef,
  hasTrackedImpression,
  createInteraction,
}) => {
  const track = useSafeTrack()
  const { trackInteraction } = useContentInteraction()
  const { trackRecommendation } = useRecommendationTracking()
  const metadata = contentLink.metadata as { episode?: Episode; project?: Project }
  const episode = metadata?.episode
  const contentId = contentLink.id ?? ''

  const handleMouseEnter = React.useCallback(() => {
    if (!contentId) return
    hoverStartTimeRef.current.set(contentId, Date.now())
  }, [contentId, hoverStartTimeRef])

  const handleMouseLeave = React.useCallback(() => {
    if (!contentId) return

    const startTime = hoverStartTimeRef.current.get(contentId)
    if (!startTime) return

    const dwellTime = Date.now() - startTime

    if (dwellTime >= HOVER_THRESHOLD_MS) {
      const interaction = createInteraction(contentLink, itemIndex)
      if (interaction) {
        trackInteraction(interaction.withHover(dwellTime))
      }
    }

    hoverStartTimeRef.current.delete(contentId)
  }, [contentId, hoverStartTimeRef, createInteraction, contentLink, itemIndex, trackInteraction])

  const handleClick = React.useCallback(() => {
    track('Top Picks For You Thumbnail Clicked', { contentId })
    trackRecommendation({
      type: 'PRESS',
      items: [{ id: contentId }],
    })

    const interaction = createInteraction(contentLink, itemIndex)
    if (interaction) {
      trackInteraction(interaction.withPress())
    }
  }, [track, contentId, trackRecommendation, createInteraction, contentLink, itemIndex, trackInteraction])

  const handleInViewChange = React.useCallback(
    (inView: boolean) => {
      if (!contentId) return

      if (inView) {
        trackRecommendation({
          type: 'IMPRESSION',
          items: [{ id: contentId }],
        })

        const timeout = setTimeout(() => {
          if (hasTrackedImpression.current.has(contentId)) return
          hasTrackedImpression.current.add(contentId)

          const interaction = createInteraction(contentLink, itemIndex)
          if (interaction) {
            trackInteraction(interaction.withImpression())
          }
        }, 1000)

        impressionTimeoutRef.current.set(contentId, timeout)
      } else {
        const timeout = impressionTimeoutRef.current.get(contentId)
        if (timeout) {
          clearTimeout(timeout)
          impressionTimeoutRef.current.delete(contentId)
        }
      }
    },
    [
      contentId,
      trackRecommendation,
      hasTrackedImpression,
      createInteraction,
      contentLink,
      itemIndex,
      trackInteraction,
      impressionTimeoutRef,
    ],
  )

  return (
    <React.Fragment key={contentLink.imageUrl}>
      <InView onChange={handleInViewChange}>
        <LandscapeTileCard
          className="mb-2 mr-4"
          linkModel={contentLink}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="absolute inset-0">
            <GuildAccessPill
              isGuildMember={isGuildMember}
              earlyAccessDate={contentLink?.watchableAt ? new Date(contentLink.watchableAt) : undefined}
            />
            {episode?.earlyAccessDate && (
              <GuildAccessNotch
                earlyAccessDate={new Date(episode.earlyAccessDate)}
                publiclyAvailableDate={
                  episode.publiclyAvailableDate ? new Date(episode.publiclyAvailableDate) : undefined
                }
                isGuildMember={isGuildMember}
                loading={loading}
              />
            )}
          </div>
          {!episode?.unavailableReason && <PlayButtonThumbnailOverlay />}
        </LandscapeTileCard>
        <TitleAndSubtitleDisplay className="mr-4" title={contentLink.eyebrow} subtitle={contentLink.subtitle} />
      </InView>
    </React.Fragment>
  )
}
